/**
 * Shared behaviour for horizontal rails: arrows, dot indicators and a
 * one-time swipe hint.
 *
 * Opt in by marking the scroller [data-calmy-rail] and giving it a sibling
 * [data-calmy-railfoot]. Everything is derived from scroll geometry, so a
 * section can add or remove cards without telling this file about it.
 *
 * The controls are only wired when the rail actually overflows. An arrow that
 * scrolls nothing, or a hint telling you to swipe a row that already fits, is
 * worse than neither.
 */
(function () {
  /* Editable wording. Sources: Theme settings > "Text: ..." groups,
     serialised by snippets/calmy-strings.liquid. The second argument is
     the wording this file shipped with and is used when the snippet is
     absent or the setting is blank. */
  var CT = function (k, f) { return window.CalmyT ? window.CalmyT(k, f) : f; };

  "use strict";

  var HINT_KEY = "calmy:railhint";

  function init(rail) {
    if (rail.__calmyRail) return;
    rail.__calmyRail = true;

    var wrap = rail.closest("[data-calmy-railwrap]") || rail.parentElement;

    /* The foot is looked up from the section, not the rail's wrapper.

       A rail usually breaks out of the page wrap so its cards can bleed to the
       screen edge, while the arrows have to line up with the heading above -
       which puts them in a different container. Searching only the wrapper
       found nothing and left the controls hidden on a rail that plainly
       overflowed. */
    var owner = rail.closest("section") || document;
    var foot = (wrap && wrap.querySelector("[data-calmy-railfoot]")) || owner.querySelector("[data-calmy-railfoot]");
    var dotwrap = foot ? foot.querySelector("[data-calmy-raildots]") : null;
    var prev = foot ? foot.querySelector('[data-calmy-railgo="prev"]') : null;
    var next = foot ? foot.querySelector('[data-calmy-railgo="next"]') : null;
    var hint = (wrap && wrap.querySelector("[data-calmy-railhint]")) || owner.querySelector("[data-calmy-railhint]");

    function overflow() { return Math.max(0, rail.scrollWidth - rail.clientWidth); }

    /* ── THE REACHABLE SCROLL POSITIONS ──────────────────────────────────────
       This is the whole fix, and it is worth being precise about what was
       wrong.

       There used to be one dot per CARD, and the active one was whichever
       card's offsetLeft sat nearest scrollLeft. But a scroller stops at
       scrollWidth - clientWidth, and every card still visible in that final
       screenful has an offsetLeft PAST that stop. So on a phone showing the
       last two products:

         - the last dot could never light, because its card's offset was
           beyond any scrollLeft the rail could reach;
         - the penultimate dot stayed lit at the end, because its card was the
           nearest reachable one;
         - and pressing either of them scrolled to a position the browser
           immediately clamped, so they did nothing visible.

       Positions are now derived from where the rail can ACTUALLY stop: each
       card's offset clamped to the maximum, then de-duplicated. Every card
       that shares the final screenful collapses into one entry, which removes
       the unreachable dots rather than leaving them to be pressed in vain.
       The first entry is pinned to 0 and the last to the maximum, so the two
       ends are exact rather than nearly right.

       It follows the cards' real offsets, so gaps, padding and percentage
       card widths are all accounted for without being named here. */
    function positions() {
      var max = overflow();
      var out = [];
      for (var i = 0; i < rail.children.length; i++) {
        var p = Math.min(Math.max(0, rail.children[i].offsetLeft - rail.clientLeft), max);
        /* 4px: two cards whose stops differ by less than that are the same
           stop as far as a finger is concerned. */
        if (!out.length || p - out[out.length - 1] > 4) out.push(p);
      }
      if (!out.length) return [0];
      out[0] = 0;
      if (out[out.length - 1] < max - 1) out.push(max);
      return out;
    }

    function nearest(list) {
      var best = 0, bestD = Infinity;
      for (var i = 0; i < list.length; i++) {
        var d = Math.abs(list[i] - rail.scrollLeft);
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    }

    /* The positions the dots were last built for. Rebuilding on count alone
       was not enough: a resize can leave the same number of cards reachable at
       different offsets, and the dots would then point at stale places. */
    var builtFor = "";

    var stops = [];

    function sync() {
      var max = overflow();
      /* 40px, not a pixel or two.

         A rail that overruns by 20px technically overflows, but arrows that
         move the row less than half a card read as broken controls rather than
         useful ones. Below this the row is treated as fitting. */
      if (foot) foot.hidden = max < 40;

      if (dotwrap) {
        /* One dot per reachable stop. Rebuilt when the stops change - which a
           resize can do without the card count changing - and not on every
           scroll frame. */
        var pos = positions();
        stops = pos;
        var key = pos.join(",");
        if (key !== builtFor) {
          builtFor = key;
          dotwrap.textContent = "";
          for (var i = 0; i < pos.length; i++) {
            var d = document.createElement("button");
            d.type = "button";
            d.className = "crail__dot";
            d.setAttribute("data-i", String(i));
            d.setAttribute("aria-label", CT("rail_go_to", "Go to item") + " " + (i + 1));
            dotwrap.appendChild(d);
          }
        }

        /* The ends are stated rather than measured. Sub-pixel scroll offsets
           and smooth-scroll easing both leave scrollLeft a fraction short of
           its target, which is enough to light the neighbouring dot at the
           very moment the rail has plainly hit the end. */
        var best;
        if (rail.scrollLeft <= 1) best = 0;
        else if (rail.scrollLeft >= max - 1) best = pos.length - 1;
        else best = nearest(pos);

        for (var k = 0; k < dotwrap.children.length; k++) {
          dotwrap.children[k].classList.toggle("is-on", k === best);
          dotwrap.children[k].setAttribute("aria-current", k === best ? "true" : "false");
        }
      }
      if (prev) prev.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= max - 2;
    }

    function step(dir) {
      // One card-width plus its gap, read from the DOM rather than assumed.
      var card = rail.firstElementChild;
      var by = card ? card.getBoundingClientRect().width + 14 : rail.clientWidth * 0.8;
      rail.scrollBy({ left: dir * by, behavior: "smooth" });
    }

    if (prev) prev.addEventListener("click", function () { step(-1); });
    if (next) next.addEventListener("click", function () { step(1); });

    /* The dots are buttons, so they act. An indicator you can see moving but
       cannot press reads as broken on a touch screen. Delegated, because the
       dots are rebuilt whenever the card count changes. */
    if (dotwrap) {
      dotwrap.addEventListener("click", function (e) {
        var d = e.target.closest(".crail__dot");
        if (!d) return;
        /* The dot's own stop, so pressing the last dot goes to the end of the
           rail rather than to a card offset the browser will clamp away. */
        var list = stops.length ? stops : positions();
        var left = list[Number(d.getAttribute("data-i"))];
        if (left != null) rail.scrollTo({ left: left, behavior: "smooth" });
      });
    }
    /* rAF-throttled: a scroll event can fire many times per frame, and running
       the same measurement three times before anything paints is pure waste.
       Passive, so the listener can never delay the scroll itself. */
    var queued = false;
    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () { queued = false; sync(); });
    }
    rail.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", sync, { passive: true });

    /* Re-measure once images have decoded - card widths are not final before
       then, and a rail measured too early reports no overflow. */
    window.addEventListener("load", sync);
    rail.querySelectorAll("img").forEach(function (im) {
      if (!im.complete) im.addEventListener("load", sync, { once: true });
    });

    sync();

    if (hint) showHint(rail, hint);
  }

  function showHint(rail, hint) {
    var seen;
    try { seen = sessionStorage.getItem(HINT_KEY); } catch (e) { seen = null; }
    if (seen) return;

    var shown = false;
    function dismiss() {
      hint.classList.remove("is-on");
      try { sessionStorage.setItem(HINT_KEY, "1"); } catch (e) {}
      rail.removeEventListener("scroll", dismiss);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting || shown) return;
        if (rail.scrollWidth - rail.clientWidth < 40) { io.disconnect(); return; }
        shown = true;
        hint.classList.add("is-on");
        setTimeout(dismiss, 2800);
        io.disconnect();
      });
    }, { threshold: 0.4 });
    io.observe(rail);

    rail.addEventListener("scroll", dismiss, { passive: true, once: true });
  }

  function boot(root) {
    (root || document).querySelectorAll("[data-calmy-rail]").forEach(init);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { boot(); });
  else boot();
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
